import React from "react";
import {
  MdEdit,
  MdDelete,
  MdFavorite,
  MdFavoriteBorder,
  MdComment,
  MdPushPin,
  MdOutlinePushPin,
} from "react-icons/md";
import Card from "../card";
import PostTypeBadge from "./PostTypeBadge";
import LinkifiedText from "./LinkifiedText";
import { Post, PostAttachment } from "../../types/community";
import { formatDateTimeWithOrdinal } from "../../utils/dateUtils";
import { getInitials } from "../../utils/formatters";

interface PostCardProps {
  post: Post;
  /** Profile image to show when the post's author has none loaded yet. */
  fallbackProfileImage?: string | null;
  canPin: boolean;
  canManage: boolean;
  pinning: boolean;
  liking: boolean;
  likeError: string | null;
  onPin: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onLike: () => void;
  onOpenLikers: () => void;
  onImageClick: (attachments: PostAttachment[], index: number) => void;
}

const PostCard: React.FC<PostCardProps> = ({
  post,
  fallbackProfileImage = null,
  canPin,
  canManage,
  pinning,
  liking,
  likeError,
  onPin,
  onEdit,
  onDelete,
  onLike,
  onOpenLikers,
  onImageClick,
}) => {
  const authorFullName = `${post.author.first_name || ""} ${
    post.author.last_name || ""
  }`.trim();
  const authorDisplayName = authorFullName || post.author.email;
  const authorProfileImage =
    post.author.profile_image?.full_path ?? fallbackProfileImage ?? null;
  const isDeletedUser = !!post.author.deleted_at;

  return (
    <Card extra="w-full p-3 md:p-6 mb-6">
      <div className="mb-4 flex items-start justify-between">
        <div className="flex flex-1 items-start space-x-4">
          <div className="flex-shrink-0">
            {authorProfileImage ? (
              <img
                src={authorProfileImage}
                alt={authorDisplayName}
                className="h-12 w-12 rounded-full object-cover"
              />
            ) : (
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-biafit-primary">
                <span className="text-lg font-medium text-white">
                  {getInitials(post.author.first_name, post.author.last_name)}
                </span>
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            {isDeletedUser && (
              <p className="mb-1 text-xs font-medium text-red-600 dark:text-red-400">
                Deleted User
              </p>
            )}
            <h2 className="mb-1 text-lg font-semibold text-navy-700 dark:text-white">
              {authorDisplayName}
            </h2>
            <div className="mb-2 flex flex-wrap items-center gap-3">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {formatDateTimeWithOrdinal(post.created_at)}
              </p>
              <PostTypeBadge type={post.type} size="sm" />
              {post.is_pinned && (
                <div className="flex items-center gap-1 rounded-full bg-blue-100 px-2 py-1 text-xs text-blue-700 dark:bg-blue-900/50 dark:text-blue-300">
                  <MdPushPin className="h-3 w-3" />
                  <span>Pinned</span>
                </div>
              )}
            </div>
            {post.challenge && (
              <div className="mb-2 block text-sm font-medium text-orange-700 dark:text-orange-300">
                <span>
                  {post.challenge.name} ({post.challenge.conducted_at_year})
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-col items-center gap-2 md:flex-row">
          {canPin && (
            <button
              onClick={onPin}
              disabled={pinning}
              className={`p-2 transition-colors ${
                post.is_pinned
                  ? "text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                  : "text-gray-600 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
              } ${pinning ? "cursor-not-allowed opacity-50" : ""}`}
              title={post.is_pinned ? "Unpin Post" : "Pin Post"}
            >
              {post.is_pinned ? (
                <MdPushPin className="h-5 w-5" />
              ) : (
                <MdOutlinePushPin className="h-5 w-5" />
              )}
            </button>
          )}
          {canManage && (
            <button
              onClick={onEdit}
              className="p-2 text-gray-600 transition-colors hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
              title="Edit Post"
            >
              <MdEdit className="h-5 w-5" />
            </button>
          )}
          {canManage && (
            <button
              onClick={onDelete}
              className="p-2 text-gray-600 transition-colors hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
              title="Delete Post"
            >
              <MdDelete className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>
      <div className="mb-4 whitespace-pre-wrap text-gray-900 dark:text-white">
        <LinkifiedText text={post.content} />
      </div>

      {post.attachments && post.attachments.length > 0 && (
        <div className="mb-6">
          <h3 className="mb-3 text-sm font-semibold text-gray-700 dark:text-gray-300">
            Attachments
          </h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {post.attachments.map((attachment, index) => (
              <div
                key={attachment.id}
                onClick={() => onImageClick(post.attachments!, index)}
                className="group relative block cursor-pointer overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700"
              >
                <img
                  src={attachment.full_path}
                  alt={attachment.original_name || "Post attachment"}
                  className="h-40 w-full object-cover transition-transform duration-200 group-hover:scale-105"
                />
                <div className="bg-black/0 group-hover:bg-black/40 absolute inset-0 transition-colors"></div>
                <div className="bg-black/60 absolute bottom-0 left-0 right-0 px-3 py-2">
                  <p className="truncate text-xs text-white">
                    {attachment.original_name || attachment.storage_name}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between border-t border-gray-200 pt-4 dark:border-gray-700">
        <div className="flex items-center space-x-6">
          <div className="flex items-center space-x-3">
            <button
              onClick={onLike}
              disabled={liking}
              className={`flex items-center justify-center rounded-full p-2 transition-colors ${
                post.is_liked_by_auth_user
                  ? "bg-red-50 text-red-500 hover:text-red-600 dark:bg-red-900/20"
                  : "bg-gray-100 text-gray-500 hover:text-red-500 dark:bg-gray-800"
              } ${liking ? "cursor-not-allowed opacity-50" : ""}`}
              aria-label={
                post.is_liked_by_auth_user ? "Unlike post" : "Like post"
              }
            >
              {post.is_liked_by_auth_user ? (
                <MdFavorite className="h-5 w-5" />
              ) : (
                <MdFavoriteBorder className="h-5 w-5" />
              )}
            </button>
            <button
              onClick={onOpenLikers}
              className="text-sm text-gray-600 transition-colors hover:text-biafit-primary dark:text-gray-300 dark:hover:text-biafit-secondary"
              aria-label="View likes"
            >
              {post.likes_count} {post.likes_count === 1 ? "like" : "likes"}
            </button>
          </div>
          <div className="flex items-center space-x-2 text-gray-500">
            <MdComment className="h-5 w-5" />
            <span>
              {post.comments_count}{" "}
              {post.comments_count === 1 ? "comment" : "comments"}
            </span>
          </div>
        </div>
      </div>
      {likeError && (
        <p className="mt-2 text-sm text-red-600 dark:text-red-400">
          {likeError}
        </p>
      )}
    </Card>
  );
};

export default PostCard;
